/**
 * Auth gate for the console. Decides chrome, not admin-ness: `/api/admin/me`
 * is the verdict, and every other request re-derives it server-side.
 */
import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { Outlet } from "react-router";

import { AdminShell } from "./components/layout/AdminShell";
import { ConsoleErrorBoundary } from "./components/error-boundary";
import { ErrorState } from "./components/error-state";
import { RouteFallback } from "./components/route-fallback";
import { Card, CardContent } from "./components/ui/card";
import { Toaster } from "./components/ui/toast";
import { gatePhase } from "./gate-phase";
import { ApiError, apiGet } from "./lib/api";
import { useAnyQueryUnauthorized } from "./lib/unauthorized";
import { SignIn } from "./routes/SignIn";
import type { Me } from "./types";

export function Gate() {
  // Always ask, even with nothing in sessionStorage. The browser login stores
  // no client-side credential at all — the session is an HttpOnly cookie the
  // SPA cannot read — so gating this query on a stored token would show the
  // sign-in screen to an operator who is already signed in.
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<Me>("/api/admin/me"),
    retry: false,
  });

  // The shell now mounts before `me` answers, so a PAGE query can be the first
  // read to find no session. That 401 is the same verdict as the gate's own.
  const pageUnauthorized = useAnyQueryUnauthorized();

  const error = me.error;
  const errorStatus = error instanceof ApiError ? error.status : undefined;
  const phase = gatePhase({
    isPending: me.isPending,
    isError: me.isError,
    errorStatus,
    hasData: me.data !== undefined,
    pageUnauthorized,
  });

  if (phase === "signin") {
    // A 401 now means one of two things this app cannot tell apart — a session
    // that expired, or a browser that never had one — because the query runs
    // before any credential exists. Claiming "expired" would greet every
    // first-time visitor with a false explanation, so the wording is the one
    // sentence true in both cases.
    return (
      <SignIn
        reason={errorStatus === 401 ? "Sign in to continue." : undefined}
      />
    );
  }

  if (phase === "error") {
    return (
      <div className="p-6">
        <Card>
          <CardContent>
            <ErrorState error={error} onRetry={() => void me.refetch()} />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <AdminShell me={me.data ?? null}>
      <Toaster>
        {/* Scoped to the page, not the shell: a crashing screen leaves the
            sidebar usable so the operator can navigate away. */}
        <ConsoleErrorBoundary>
          {/* THE boundary for route code-splitting: every page in
              `console-routes.tsx` is `React.lazy`, so the first visit to a
              screen suspends here while its chunk downloads. One boundary
              around the <Outlet /> rather than one per route — the shell,
              the sidebar and the toaster all stay mounted through it, so a
              page change never looks like a reload. */}
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </ConsoleErrorBoundary>
      </Toaster>
    </AdminShell>
  );
}
